const API_BASE = '/api/v1/ibkr';

// Client-seitiges Timeout, knapp ueber dem Backend-Handler-Timeout (55 s)
// und dem Nginx-Default (60 s). Faellt das Netz aus oder haengt die
// Verbindung, bricht AbortController die Anfrage sauber ab, statt den Tab
// minutenlang warten zu lassen (Fix fuer L12 V6 aus dem Audit).
const REQUEST_TIMEOUT_MS = 65_000;

/**
 * Deutsche, lesbare Fehlermeldung aus einer HTTP-Response extrahieren.
 * Backend liefert bei 504/503/500 JSON `{ "detail": "..." }` — das hatte
 * bisher der Alert als rohen Text-Blob gerendert (L12 V6). Jetzt parsen
 * wir das JSON und bauen eine menschen-lesbare Zeile.
 */
async function _parseErrorMessage(response: Response): Promise<string> {
  let body = '';
  try {
    body = await response.text();
  } catch {
    /* ignoriert — dann bleibt body leer */
  }
  // Nginx-eigene HTML-Seiten (z.B. 504 Gateway Timeout) erkennen und
  // durch eine kurze deutsche Meldung ersetzen, statt 2 KB HTML
  // anzuzeigen.
  if (body.startsWith('<') || body.includes('<html')) {
    if (response.status === 504) {
      return 'Zeitüberschreitung: IBKR oder das Gateway antworten gerade nicht. Bitte in ein paar Sekunden erneut versuchen.';
    }
    return `Server-Fehler ${response.status} (${response.statusText || 'unbekannt'}).`;
  }
  // JSON mit "detail"-Feld (FastAPI-Standard) parsen
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed.detail === 'string') return parsed.detail;
  } catch {
    /* kein JSON — body ist ggf. schon lesbarer Text */
  }
  return body || `Fehler ${response.status}`;
}

async function makeRequest(path: string, options?: RequestInit) {
  const stored = localStorage.getItem('authTokens');
  const token = stored ? JSON.parse(stored).access_token : null;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    REQUEST_TIMEOUT_MS,
  );
  try {
    const response = await fetch(API_BASE + path, {
      signal: controller.signal,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    // BUG-IBKR-37: Backend gibt HTTP 400 bei Fehlern. Wir pruefen
    // zusaetzlich den Body auf "error"-Key als Fallback.
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      // Bei HTML- oder Gateway-Fehlern liefert _parseErrorMessage eine
      // lesbare Meldung; bei strukturiertem JSON-Error bevorzugen wir
      // data.error/data.detail.
      if (data && (data.error || data.detail)) {
        throw new Error(data.error || data.detail);
      }
      throw new Error(await _parseErrorMessage(response));
    }
    if (data?.error) {
      throw new Error(data.error);
    }
    return data;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error(
        'Zeitüberschreitung: die Anfrage hat länger als '
          + `${REQUEST_TIMEOUT_MS / 1000}s gedauert und wurde abgebrochen.`,
      );
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

// ── Chart types (co-located with the service that owns them) ───────────────

export interface ChartBar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export interface RegimeZone {
  from: number;
  to: number;
  regime: string;
}

export interface StrikeMarker {
  strike: number;
  type: string;
  action: string;
  expiry: string;
}

export interface ChartResponse {
  symbol: string;
  timeframe: string;
  bars: ChartBar[];
  regime_zones: RegimeZone[];
  strike_markers: StrikeMarker[];
}

// ──────────────────────────────────────────────────────────────────────────

export const ibkrService = {
  getStatus: () => makeRequest('/status'),
  connect: () => makeRequest('/connect', { method: 'POST' }),
  disconnect: () => makeRequest('/disconnect', { method: 'POST' }),
  getAnalysis: () => makeRequest('/analysis'),
  refreshAnalysis: () => makeRequest('/analysis/refresh', { method: 'POST' }),
  getPositions: () => makeRequest('/positions'),
  executeSignal: (symbol: string) => makeRequest('/execute', { method: 'POST', body: JSON.stringify({ symbol }) }),
  closePosition: (positionId: string, opts?: {
    orderType?: 'MKT' | 'LMT' | 'AUTO';
    limitPrice?: number;
    outsideRth?: boolean;
  }) => makeRequest('/close', {
    method: 'POST',
    body: JSON.stringify({
      position_id: positionId,
      order_type: opts?.orderType ?? 'MKT',
      limit_price: opts?.limitPrice,
      outside_rth: opts?.outsideRth ?? false,
    }),
  }),
  getHistory: () => makeRequest('/history'),
  getSettings: () => makeRequest('/settings'),
  updateSettings: (settings: any) => makeRequest('/settings', { method: 'POST', body: JSON.stringify(settings) }),
  startAuto: () => makeRequest('/auto/start', { method: 'POST' }),
  stopAuto: () => makeRequest('/auto/stop', { method: 'POST' }),
  getChart: (symbol: string, timeframe: string): Promise<ChartResponse> =>
    makeRequest(`/chart/${encodeURIComponent(symbol)}?timeframe=${encodeURIComponent(timeframe)}`),
  getOpenOrders: () => makeRequest('/orders'),
  cancelOrder: (orderId: number) =>
    makeRequest('/orders/cancel', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId }),
    }),
  // Depot-Log
  getDepotLog: () => makeRequest('/depot-log'),
  setDepotStart: (startDate: string) =>
    makeRequest('/depot-log/start', {
      method: 'POST',
      body: JSON.stringify({ start_date: startDate }),
    }),
  resetDepotLog: () =>
    makeRequest('/depot-log/reset', { method: 'POST' }),
};
